import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material";

import usePagination from "../../../../hooks/usePagination";
import { FAILED_COLUMNS } from "../HeliosFailedSearch/constants";
import { FilterAutocomplete, Table } from "../../../../components/ui";
import { WITHDRAWALS_COLUMNS } from "../WithdrawalsSearch/constants";
import { complianceSearchActions } from "../../../../reducers/complianceSearch";
import { appendSearchParamsToQuery } from "../../utils";
import { useComplianceSearchContext } from "../../../../context";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../../api/compliance";
import {
  PASSES_COLUMNS,
  TCP_STEPS_COLUMNS,
  GM_CAPPERS_COLUMNS,
  HELIOS_LIVE_TIF_COLUMNS,
  HELIOS_LIVE_SIM_TIF_COLUMNS,
} from "../../constants";
import AddAccountInGroup from "../../../FundedCompliance/components/AddAccountInGroup";
import { getEntriesIdWithPrefix } from "../../../FundedCompliance/components/EntriesIdWithPrefix";
import { IColum } from "../../../../utils/copyToClipboardInXlsFormat";

const TableWrapper = styled("div")(() => ({
  display: "grid",
  gridTemplateColumns: "49% 49%",
  columnGap: "2%",
}));

const ENTRIES_ID_COLUMN: IColum = {
  by: "entries_id",
  title: "Entries ID",
  path: "entries_id",
  //@ts-ignore
  getCellValue: (complianceTraderId: number): React.ReactElement => {
    return <AddAccountInGroup complianceTraderId={complianceTraderId} isDisableEdit={false} />;
  },
  //@ts-ignore
  copyToClipBoardCallBack: (value: number): string => getEntriesIdWithPrefix(value),
};

const FundedAccountsInvestigationSearch: React.FC = () => {
  const pagination = usePagination();
  const dispatch = useDispatch();

  const { tabsTablesSearchState, setTabsTablesSearchState } = useComplianceSearchContext();
  const { search } =
    tabsTablesSearchState.fundedAccountInvestigationTab.fundedAccountsInvestigationSearchTable;

  const {
    loading,
    data: { passes, liveSimTif, liveTif, withdrawals, watchlistFailed, gm_cappers, tcp_steps },
  } = useSelector((state) => state.complianceSearch.heliosAccountsInvestigation);

  const { isError, error } = useQuery({
    retry: false,
    queryKey: [
      COMPLIANCE_API_URLS.fundedAccountInvestigation,
      Object.values(search),
      pagination.page,
      pagination.limit,
    ],
    async queryFn({ signal }) {
      const query = new URLSearchParams();
      query.append("limit", pagination.limit.toString());
      query.append("page", pagination.page.toString());
      appendSearchParamsToQuery(search, query);

      dispatch(complianceSearchActions.fetchHeliosAccountsInvestigationRequest());
      const response = await ComplianceAPI.getFundedAccountInvestigationRequest(
        query.toString(),
        signal
      );
      pagination.setCount(response.data.passes.count);
      dispatch(complianceSearchActions.fetchHeliosAccountsInvestigationSuccess(response.data));
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching getHeliosRelatedSearch", error);
      dispatch(complianceSearchActions.fetchHeliosAccountsInvestigationFailed());
    }
  }, [isError, error]);

  useEffect(() => {
    pagination.setPage(0);
  }, [search, pagination.limit]);

  return (
    <div>
      <div>
        <FilterAutocomplete
          search={search}
          setSearch={(values) => {
            setTabsTablesSearchState({
              ...tabsTablesSearchState,
              fundedAccountInvestigationTab: {
                ...tabsTablesSearchState.fundedAccountInvestigationTab,
                fundedAccountsInvestigationSearchTable: {
                  search: values,
                },
              },
            });
          }}
          filters={{
            Name: "names",
            Email: "email",
            Phone: "phone",
            Address: "address",
            EntriesId: "compliance_entries_id",
          }}
        />
      </div>

      <div>
        <Table
          columns={[ENTRIES_ID_COLUMN, ...PASSES_COLUMNS]}
          data={passes.rows}
          loading={loading}
          extractKey={(row) => row.id}
          theadTitle="Passes"
          pagination={pagination}
          csv_table_name="Funded account investigation - Passes"
          noResultsMessage="No funded accounts investigation found"
        />
      </div>

      <TableWrapper>
        <div>
          <Table
            columns={[ENTRIES_ID_COLUMN, ...GM_CAPPERS_COLUMNS]}
            data={gm_cappers.rows}
            loading={loading}
            extractKey={(row) => row.id}
            theadTitle="Gaunt Cappers"
            csv_table_name="Funded account investigation - Gaunt Cappers"
            noResultsMessage="No Gaunt Cappers found"
          />
        </div>
        <div>
          <Table
            columns={[ENTRIES_ID_COLUMN, ...TCP_STEPS_COLUMNS]}
            data={tcp_steps.rows}
            loading={loading}
            extractKey={(row) => row.id}
            theadTitle="TCP steps"
            csv_table_name="Funded account investigation - TCP steps"
            noResultsMessage="No TCP steps found"
          />
        </div>
      </TableWrapper>

      <TableWrapper>
        <div>
          <Table
            columns={[ENTRIES_ID_COLUMN, ...WITHDRAWALS_COLUMNS]}
            data={withdrawals.rows}
            loading={loading}
            extractKey={(row) => row.id}
            theadTitle="Withdrawals"
            csv_table_name="Funded account investigation - Withdrawals"
            noResultsMessage="No Withdrawals found"
          />
        </div>
        <div>
          <Table
            columns={[ENTRIES_ID_COLUMN, ...FAILED_COLUMNS]}
            data={watchlistFailed.rows}
            loading={loading}
            extractKey={(row) => row.id}
            theadTitle="Failed"
            csv_table_name="Funded account investigation - Failed"
            noResultsMessage="No Failed records found"
          />
        </div>
      </TableWrapper>

      <TableWrapper>
        <div>
          <Table
            columns={[ENTRIES_ID_COLUMN, ...HELIOS_LIVE_SIM_TIF_COLUMNS]}
            data={liveSimTif.rows}
            loading={loading}
            extractKey={(row) => row.id}
            theadTitle="Live Sim TIF"
            csv_table_name="Funded account investigation - Live Sim TIF"
            noResultsMessage="No Live Sim TIF records found"
          />
        </div>
        <div>
          <Table
            columns={[ENTRIES_ID_COLUMN, ...HELIOS_LIVE_TIF_COLUMNS]}
            data={liveTif.rows}
            loading={loading}
            extractKey={(row) => row.id}
            theadTitle="Live TIF"
            csv_table_name="Funded account investigation - Live TIF"
            noResultsMessage="No Live TIF records found"
          />
        </div>
      </TableWrapper>
    </div>
  );
};

export default FundedAccountsInvestigationSearch;
